import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  EffectRef,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  Signal,
  signal
} from '@angular/core';
import { inlineHtml } from '@core/utilities';
import {
  DEFAULT_TOAST_OPTIONS,
  ToastAnimation,
  ToastAutoDismissDuration,
  ToastPlacement,
  ToastPopover,
  ToastRole,
  ToastSupport
} from './index';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/** Toast Auto-dismiss configuration shape */
interface ToastAutoDismiss {
  percent: number;
  loop: {
    interval: number;
    id: number;
  };
  durationMilliseconds: {
    full: number;
    current: number;
  };
}

/**
 * Toast host. Normally created by `Toast` service programmatically, but also works declaratively:
 * `<output oke-toast
 *   popover
 *   aria-live="polite"
 *   id=""
 *   class="is-<value>" // alert | success | info | warning
 *   title=""
 *   body=""
 *   [is-closable]='false' // default
 *   [font-size-base]='.625rem' // default
 * ></output>`
 * (it opens itself after first render and emits `closed` once fully dismissed).
 */
@Component({
  selector: 'output[oke-toast]',
  templateUrl: './toast.html',
  styleUrl: './toast.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--toast-auto-dismiss-pct]': '$autoDismiss() ? $autoDismissConfig().percent : null',
    '[style.--font-size-base]': '$fontSizeBase()',
    '[id]': '$id()',
    '[class]': '$classNames() ? $classNames() : null',
    '[attr.role]': '$role()',
    '[attr.popover]': '$popover()',
    '[attr.data-animation]': '$animation()',
    '[attr.data-auto-dismiss]': '$autoDismiss() ? $autoDismiss() : null',
    '[attr.data-placement]': '$placement()',
    '[attr.data-ready]': '$isReady() ? "" : null',
    '(mouseenter)': '$autoDismiss() ? pauseAutoDismiss() : null',
    '(mouseleave)': '$autoDismiss() ? resumeAutoDismiss() : null',
    '(toggle)': 'onToggle($event)'
  }
})
export class Toast {
  private readonly _host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly _document = inject(DOCUMENT);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _timerEffectRef: EffectRef;
  private _frame = 0;
  private _isDismissed = false;
  private _isFinished = false;
  /** Required: referenced by the dismiss button's `popovertarget`. */
  readonly $id = input.required<string>({
    alias: 'id'
  });
  readonly $title = input.required<string>({
    alias: 'title'
  });
  readonly $body = input.required<string>({
    alias: 'body'
  });
  readonly $role = input<ToastRole>(DEFAULT_TOAST_OPTIONS.role, {
    alias: 'role'
  });
  readonly $popover = input<ToastPopover>(DEFAULT_TOAST_OPTIONS.popover, {
    alias: 'popover'
  });
  readonly $animation = input<ToastAnimation>(DEFAULT_TOAST_OPTIONS.animation, {
    alias: 'animation'
  });
  readonly $classNames = input<string[] | null, string[] | string | null>(
    typeof DEFAULT_TOAST_OPTIONS.classNames === 'string'
      ? [DEFAULT_TOAST_OPTIONS.classNames]
      : DEFAULT_TOAST_OPTIONS.classNames,
    {
      alias: 'classNames',
      transform: (value: unknown): string[] | null => {
        if (value === null || value === undefined) {
          return null;
        }
        if (Array.isArray(value)) {
          return value;
        }
        return [value as string];
      }
    }
  );
  // eslint-disable-next-line local/signal-notation
  readonly $autoDismiss = input<ToastAutoDismissDuration | false, unknown>(DEFAULT_TOAST_OPTIONS.autoDismiss, {
    alias: 'autoDismiss',
    transform: (value: unknown): ToastAutoDismissDuration | false => {
      const isFalse = value === false;
      const numericalPart = !isFalse ? Number((value as string).replace(/(ms|s)$/, '')) : false;
      if (value === false || numericalPart === false || numericalPart < 0) {
        return false;
      }
      return value as ToastAutoDismissDuration;
    }
  });
  readonly $placement = input<ToastPlacement>(DEFAULT_TOAST_OPTIONS.placement, {
    alias: 'placement'
  });
  readonly $support = input<ToastSupport | null>(null, {
    alias: 'support'
  });
  // Intended for declarative implementation(s)
  readonly $fontSizeBase = input<string>('.625rem', {
    alias: 'font-size-base'
  });
  readonly $isClosable = input<boolean>(false, {
    alias: 'is-closable'
  });
  protected readonly $supportText: Signal<SafeHtml | string> = computed(() => {
    const $support = this.$support();
    let supportText = '';
    if (typeof $support === 'object' && $support !== null) {
      if ($support.shouldShowHelpCenter) {
        supportText = 'Click <a href="/help">here</a> for support';
        if ($support.shouldShowServiceDesk) {
          supportText +=
            ', and or <a href="tel:+18336636548" data-tooltip="(833) 663-6548">call</a> the ONEOK Service Desk';
        }
      } else if ($support.shouldShowServiceDesk) {
        supportText =
          '<a href="tel:+18336636548" data-tooltip="(833) 663-6548">Call</a> the ONEOK Service Desk for support';
      }
      if ($support.shouldShowHelpCenter || $support.shouldShowServiceDesk) {
        supportText += '.';
      }
    }
    return supportText !== '' ? this._sanitizer.bypassSecurityTrustHtml(supportText) : supportText;
  });
  /** Emits once the toast is hidden and any exit animation has finished. */
  readonly closed = output<void>();
  /**
   * Content is committed only after the (empty) live region is already open in the
   * accessibility tree. Screen readers announce *changes* to a live region far more
   * reliably than a region that appears with its content already inside it.
   */
  private readonly _$isReady = signal(false);
  protected $isReady = this._$isReady.asReadonly();
  protected readonly $titleHtml = computed(() => inlineHtml.toInlineHtml(this.$title(), this._document));
  protected readonly $bodyHtml = computed(() => inlineHtml.toInlineHtml(this.$body(), this._document));
  /** Configuration object manages the auto-hide feature, if enabled */
  readonly $autoDismissConfig = linkedSignal<ToastAutoDismiss>(() => ({
    percent: 1,
    loop: {
      interval: 50,
      id: 0
    },
    durationMilliseconds: {
      full: this._getAutoDismissComputedMilliseconds(this.$autoDismiss()),
      current: this._getAutoDismissComputedMilliseconds(this.$autoDismiss())
    }
  }));

  constructor() {
    afterNextRender({ write: () => this._open() });
    inject(DestroyRef).onDestroy(() => {
      [this._frame, this.$autoDismissConfig().loop.id].forEach(animationFrame => {
        cancelAnimationFrame(animationFrame);
      });
    });
    this._timerEffectRef = effect(() => {
      if (this._$isReady()) {
        if (this.$autoDismiss()) {
          this._startTimer();
          this._timerEffectRef.destroy();
        }
        this._timerEffectRef.destroy();
      }
    });
  }

  get hostClasses(): string[] {
    return Array.from(this._host.classList).map(className => className.replace('is-', ''));
  }

  dismiss(): void {
    this._isDismissed = true;
    if (this._host.matches(':popover-open')) {
      this._host.hidePopover(); // routed through onToggle, same as the native button / light dismiss
    } else {
      this._finish(); // dismissed before it ever opened
    }
  }

  protected pauseAutoDismiss(): void {
    cancelAnimationFrame(this.$autoDismissConfig().loop.id);
  }

  protected declarativeDismiss(): void {
    this._finish();
  }

  protected resumeAutoDismiss(): void {
    this._startTimer();
  }

  private _finish(): void {
    if (this._isFinished) {
      return;
    }
    this._isFinished = true;
    this.closed.emit();
  }

  private _getAutoDismissComputedMilliseconds(autoDismissValue: ToastAutoDismissDuration | false): number {
    let autoDismissMilliseconds = 0;
    if (autoDismissValue) {
      autoDismissMilliseconds =
        (autoDismissValue as ToastAutoDismissDuration).search('ms') >= 1
          ? Number((autoDismissValue as ToastAutoDismissDuration).replace('ms', ''))
          : Number((autoDismissValue as ToastAutoDismissDuration).replace('s', '')) * 1_000;
    }
    return autoDismissMilliseconds;
  }

  private _getTimer(lastTime: DOMHighResTimeStamp = 0): (timestamp: number) => void {
    const loop = (timestamp: DOMHighResTimeStamp): void => {
      if (!lastTime) {
        lastTime = timestamp;
      }
      const elapsed = timestamp - lastTime;
      if (elapsed >= this.$autoDismissConfig().loop.interval) {
        this.$autoDismissConfig.update(config => {
          const { durationMilliseconds } = config;
          const current = durationMilliseconds.current - elapsed;
          return {
            ...config,
            durationMilliseconds: {
              ...durationMilliseconds,
              current
            },
            percent: parseFloat((current / config.durationMilliseconds.full).toFixed(3))
          };
        });
        lastTime = timestamp - (elapsed % this.$autoDismissConfig().loop.interval);
      }
      if (this.$autoDismissConfig().durationMilliseconds.current <= 0) {
        cancelAnimationFrame(this.$autoDismissConfig().loop.id);
        this.dismiss();
      } else {
        this.$autoDismissConfig.update(config => {
          return {
            durationMilliseconds: config.durationMilliseconds,
            percent: config.percent,
            loop: {
              id: requestAnimationFrame(loop),
              interval: config.loop.interval
            }
          };
        });
      }
    };
    return loop;
  }

  protected async onToggle(event: ToggleEvent): Promise<void> {
    if (event.newState !== 'closed') {
      return;
    }
    if (this.$autoDismiss()) {
      cancelAnimationFrame(this.$autoDismissConfig().loop.id);
    }
    // getAnimations() flushes style, so the exit transition (if any) is already running here.
    await Promise.allSettled(this._host.getAnimations().map(animation => animation.finished));
    this._finish();
  }

  private _open(): void {
    if (this._isDismissed || !this._host.isConnected || this._host.matches(':popover-open')) {
      return;
    }
    if (!!this.$popover() === true) {
      this._host.showPopover();
    }
    // Two frames: the open-but-empty state is styled and exposed before content lands,
    // which also gives the enter transition a committed start state.
    this._frame = requestAnimationFrame(() => {
      this._frame = requestAnimationFrame(() => this._$isReady.set(true));
    });
  }

  private _startTimer(): void {
    if (this.$autoDismiss() === false) {
      return;
    }
    this.$autoDismissConfig.update(config => {
      return {
        percent: config.percent,
        durationMilliseconds: config.durationMilliseconds,
        loop: {
          interval: config.loop.interval,
          id: requestAnimationFrame(this._getTimer())
        }
      };
    });
  }
}
